import React from "react";
import {
  Routes as Switch,
  Route,
  Navigate,
  createBrowserRouter,
} from "react-router-dom";
import NotFound from "components/NotFound";
import SWSearch from "components/sw/search/SWSearch";
import UserProfile from "components/user/UserProfile";
import ManageSW from "components/sw/manage/ManageSW";
import UserManagement from "components/user/management/UserManagement";
import EditUser from "components/user/management/EditUser";
import UserDetail from "components/user/management/UserDetail";
import MyApprovals from "components/approvals/mine/MyApprovals";
import ViewApproval from "components/approvals/view/ViewApproval";
import ViewBatchApproval from "components/approvals/view/batch/ViewBatchApproval";
import ViewRCUpdateApproval from "components/approvals/view/rcupdate/ViewRCUpdateApproval";
import { AuthCheck, AuthFilter } from "auth/AuthFilter";
import { Roles } from "interfaces/user/UserInterfaces";
import AuthRoute from "./AuthRoute";
import BatchUpdatesCreator from "components/batchUpdates/BatchUpdatesCreator";
import ManageSwReports from "components/sw/reports/ManageSwReports";
import DownloadLatestPdfVersion from "components/sw/reports/DownloadLatestPdfVersion";
import NewUserRoleAssignments from "../user/management/NewUserRoleAssignments";
import EditRoleAssignment from "../user/EditRoleAssignment";
import useSelector from "store/useSelector";
import DownloadLatestPdfByGuid from "components/sw/reports/DownloadLatestPdfByGuid";
import RequestSystemAccess from "../user/systemAcesss/RequestSystemAccess";
import SystemAccessRoleAssignmentForm from "../user/systemAcesss/SystemAccessRoleAssignmentForm";
import ViewSystemAccessRequest from "../user/systemAcesss/ViewSystemAccessRequest";
import ViewSystemAccessRequestRoleAssignment from "../user/systemAcesss/ViewSystemAccessRequestRoleAssignment";
import DownloadLatestPdfByGuidAndType from "components/sw/reports/DownloadLatestPdfByGuidAndType";
import PdfReadyPage from "components/pdfReady/PdfReadyPage";
import { TooltipArea, TooltipContainer } from "components/common/Tooltip";
import { ApprovalTooltipArea, ApprovalTooltipContainer } from "components/approvals/history/ApprovalTooltip";
import Layout from "components/layout/Layout";
import TCCModal from "components/common/TCCModal";
import GlobalComponents from "components/common/GlobalComponents";
import ProcessZipModal from "components/common/ProcessZipModal";
import ProcessTimeRCModal from "components/common/ProcessTimeRCModal";

export enum Routes {
  NewStandardWork = "/sw/new/:mode",
  EditStandardWork = "/sw/:guid/:version/edit",
  ViewStandardWork = "/sw/:guid/:version/view",
  MyProfile = "/user/profile",
  Root = "/",
  SearchStandardWork = "/sw/search",
  UserManagement = "/users/manage",
  EditUser = "/users/manage/getUserEditInfo/:email/:role/:isTechUser/:isPsdUser/:isMfgUser/:isRCEditor",
  EditRoleAssignment = "/users/manage/editRoleAssignment",
  SystemAccessEditRoleAssignment = "/systemAccess/request/roleAssignment",
  NewUserRoleAssignments = "/users/manage/newUserRoleAssignments",
  RequestSystemAccess = "/systemAccess/request",
  ViewSystemAccessRequest = "/systemAccess/view/:id",
  ViewSystemAccessRequestRoleAssignment = "/systemAccess/view/:id/roleAssignment",
  UserDetail = "/users/detail/:email",
  MyApprovals = "/approvals",
  ViewApproval = "/approvals/:id",
  ViewBatchApproval = "/approvals/batch/:id",
  ViewRCUpdateApproval = "/approvals/rcupdate/:id",
  BatchUpdatesCreator = "/batchUpdates/new",
  Reports = "/Pdfs",
  DownloadLatestPdfVersion = "/Pdfs/:swId",
  DownloadLatestPdfByGuid = "/:swGuid/Pdfs",
  DownloadLatestPdfByGuidAndType = "/:swGuid/:pdfType/Pdfs",
  PdfReady = "/pdf-ready/:jobId"
}

const router = createBrowserRouter([
  {
    path: "/",
    element: <TooltipArea>
      <ApprovalTooltipArea>
        <Layout />
        <TCCModal />
        <GlobalComponents />
        <ProcessZipModal />
        <ProcessTimeRCModal />
        <TooltipContainer />
        <ApprovalTooltipContainer />
      </ApprovalTooltipArea>
    </TooltipArea>,
    children: [

      { index: true, element: <Navigate to={Routes.SearchStandardWork} replace /> },
      {
        path: Routes.SearchStandardWork, element: <AuthCheck authFilter={AuthFilter.isLoggedIn()
          .hasAnyRoleIn([
            Roles.Author,
            Roles.ApproverCOE,
            Roles.ApproverTechContent,
            Roles.ApproverOwner,
            Roles.OrgAdmin,
            Roles.TechComm,
            Roles.ProxyOwner,
          ])
          .includeSysAdmins()} show403OnFail={true}>
          <SWSearch />
        </AuthCheck>
      },


      {
        path: Routes.NewStandardWork, element: <AuthCheck authFilter={AuthFilter.isLoggedIn()
          .hasAnyRoleIn([
            Roles.Author,
            Roles.OrgAdmin,
            Roles.TechComm,
          ])
          .includeSysAdmins()} show403OnFail={true}>
          <ManageSW />
        </AuthCheck>
      },

      {
        path: Routes.EditStandardWork, element: <AuthCheck authFilter={AuthFilter.isLoggedIn()
          .hasAnyRoleIn([
            Roles.Author,
            Roles.OrgAdmin,
            Roles.TechComm,
            Roles.ApproverOwner,
            Roles.ApproverCOE,
            Roles.ApproverTechContent,
            Roles.ProxyOwner,
            Roles.SMEApprover,
            Roles.ApproverTechContent,
            Roles.TechConRev
          ])
          .includeSysAdmins()} show403OnFail={true}>
          <ManageSW />
        </AuthCheck>
      },

      {
        path: Routes.ViewStandardWork, element: <AuthCheck authFilter={AuthFilter.isLoggedIn()
          .hasAnyRoleIn([
            Roles.Author,
            Roles.OrgAdmin,
            Roles.TechComm,
            Roles.ApproverOwner,
            Roles.ApproverCOE,
            Roles.ApproverTechContent,
            Roles.ProxyOwner,
            Roles.TechConRev,
            Roles.SMEApprover
          ])
          .includeSysAdmins()} show403OnFail={true}>
          <ManageSW />
        </AuthCheck>
      },

      { path: Routes.MyProfile, element: <UserProfile /> },

      {
        path: Routes.UserManagement, element: <AuthCheck authFilter={AuthFilter.isLoggedIn()
          .hasRole(Roles.OrgAdmin)
          .includeSysAdmins()} show403OnFail={true}>
          <UserManagement />
        </AuthCheck>
      },

      {
        path: Routes.EditUser, element: <AuthCheck authFilter={AuthFilter.isLoggedIn()
          .hasRole(Roles.OrgAdmin)
          .includeSysAdmins()} show403OnFail={true}>
          <EditUser />
        </AuthCheck>
      },

      {
        path: Routes.NewUserRoleAssignments, element: <AuthCheck authFilter={AuthFilter.isLoggedIn()
          .hasRole(Roles.OrgAdmin)
          .includeSysAdmins()} show403OnFail={true}>
          <NewUserRoleAssignments />
        </AuthCheck>
      },

      {
        path: Routes.RequestSystemAccess, element: <AuthCheck authFilter={AuthFilter.isLoggedIn()
          .includeSysAdmins()} show403OnFail={true}>
          <RequestSystemAccess />
        </AuthCheck>
      },

      {
        path: Routes.ViewSystemAccessRequest, element: <AuthCheck authFilter={AuthFilter.isLoggedIn()
          .hasRole(Roles.OrgAdmin)
          .includeSysAdmins()} show403OnFail={true}>
          <ViewSystemAccessRequest />
        </AuthCheck>
      },

      {
        path: Routes.SystemAccessEditRoleAssignment, element: <AuthCheck authFilter={AuthFilter.isLoggedIn()} show403OnFail={true}>
          <SystemAccessRoleAssignmentForm />
        </AuthCheck>
      },

      {
        path: Routes.ViewSystemAccessRequestRoleAssignment, element: <AuthCheck authFilter={AuthFilter.isLoggedIn()} show403OnFail={true}>
          <ViewSystemAccessRequestRoleAssignment />
        </AuthCheck>
      },

      {
        path: Routes.EditRoleAssignment, element: <AuthCheck authFilter={AuthFilter.isLoggedIn()
          .hasRole(Roles.OrgAdmin)
          .includeSysAdmins()} show403OnFail={true}>
          <EditRoleAssignment onInvalidSelectedUserRoute={Routes.NewUserRoleAssignments} redirectRoute={Routes.NewUserRoleAssignments.toString()} />
        </AuthCheck>
      },

      {
        path: Routes.UserDetail, element: <AuthCheck authFilter={AuthFilter.isLoggedIn()
          .hasRole(Roles.OrgAdmin)
          .includeSysAdmins()} show403OnFail={true}>
          <UserDetail />
        </AuthCheck>
      },

      {
        path: Routes.MyApprovals, element: <AuthCheck authFilter={AuthFilter.isLoggedIn()
          .hasAnyRoleIn([
            Roles.Author,
            Roles.ApproverCOE,
            Roles.ApproverTechContent,
            Roles.ApproverOwner,
            Roles.OrgAdmin,
            Roles.TechComm,
            Roles.TechConRev,
            Roles.SMEApprover,
            Roles.ProxyOwner,
          ])
          .includeSysAdmins()} show403OnFail={true}>
          <MyApprovals />
        </AuthCheck>
      },

      {
        path: Routes.ViewApproval, element: <AuthCheck authFilter={AuthFilter.isLoggedIn()
          .hasAnyRoleIn([
            Roles.Author,
            Roles.ApproverCOE,
            Roles.ApproverTechContent,
            Roles.ApproverOwner,
            Roles.OrgAdmin,
            Roles.TechComm,
            Roles.TechConRev,
            Roles.SMEApprover,
            Roles.ProxyOwner,
          ])
          .includeSysAdmins()} show403OnFail={true}>
          <ViewApproval />
        </AuthCheck>
      },

      {
        path: Routes.ViewBatchApproval, element: <AuthCheck authFilter={AuthFilter.isLoggedIn()
          .hasAnyRoleIn([
            Roles.Author,
            Roles.ApproverCOE,
            Roles.ApproverTechContent,
            Roles.ApproverOwner,
            Roles.OrgAdmin,
            Roles.TechComm,
            Roles.TechConRev,
            Roles.SMEApprover,
            Roles.ProxyOwner,
          ])
          .includeSysAdmins()} show403OnFail={true}>
          <ViewBatchApproval />
        </AuthCheck>
      },


      {
        path: Routes.ViewRCUpdateApproval, element: <AuthCheck authFilter={AuthFilter.isLoggedIn()
          .hasAnyRoleIn([
            Roles.Author,
            Roles.ApproverCOE,
            Roles.ApproverTechContent,
            Roles.ApproverOwner,
            Roles.OrgAdmin,
            Roles.TechComm,
            Roles.TechConRev,
            Roles.SMEApprover,
            Roles.ProxyOwner,
          ])
          .includeSysAdmins()} show403OnFail={true}>
          <ViewRCUpdateApproval />
        </AuthCheck>
      },

      {
        path: Routes.BatchUpdatesCreator, element: <AuthCheck authFilter={AuthFilter.isLoggedIn()
          .hasAnyRoleIn([
            Roles.Author,
            Roles.OrgAdmin,
          ]).includeSysAdmins()} show403OnFail={true}>
          <BatchUpdatesCreator />
        </AuthCheck>
      },

      {
        path: Routes.Reports, element: <AuthCheck authFilter={AuthFilter.isLoggedIn()
          .hasAnyRoleIn([
            Roles.ReportViewer,
          ]).includeSysAdmins()} show403OnFail={true}>
          <ManageSwReports />
        </AuthCheck>
      },

      {
        path: Routes.DownloadLatestPdfVersion, element: <AuthCheck authFilter={AuthFilter.isLoggedIn()
          .hasAnyRoleIn([
            Roles.ReportViewer,
          ]).includeSysAdmins()} show403OnFail={true}>
          <DownloadLatestPdfVersion />
        </AuthCheck>
      },

      {
        path: Routes.DownloadLatestPdfByGuid, element: <AuthCheck authFilter={AuthFilter.isLoggedIn()
          .hasAnyRoleIn([
            Roles.ReportViewer,
          ]).includeSysAdmins()} show403OnFail={true}>
          <DownloadLatestPdfByGuid />
        </AuthCheck>
      },

      {
        path: Routes.DownloadLatestPdfByGuidAndType, element: <AuthCheck authFilter={AuthFilter.isLoggedIn()
          .hasAnyRoleIn([
            Roles.ReportViewer,
            Roles.Author
          ])} show403OnFail={true}>
          <DownloadLatestPdfByGuidAndType />
        </AuthCheck>
      },

      {
        path: Routes.PdfReady, element: <AuthCheck authFilter={AuthFilter.isLoggedIn()} show403OnFail={true}>
          <PdfReadyPage />
        </AuthCheck>
      },

      { path: "*", element: <NotFound /> }


    ]
  }
])


export default router;